import type { Ref } from 'vue';

import { useIntersectionObserver } from '@vueuse/core';
import { computed, nextTick, ref, watch } from 'vue';

interface UseShortsListingInfiniteScrollOptions {
	loadMoreRef: Ref<HTMLElement | null>;
	hasMore: Ref<boolean>;
	isLoading: Ref<boolean>;
	isInfiniteLoading: Ref<boolean>;
	blockListingFetches: Ref<boolean>;
	onLoadMore: () => boolean | Promise<boolean>;
	rootMargin?: string;
	threshold?: number | number[];
}

export function useShortsListingInfiniteScroll({
	loadMoreRef,
	hasMore,
	isLoading,
	isInfiniteLoading,
	blockListingFetches,
	onLoadMore,
	rootMargin = '0px',
	threshold = 0
}: UseShortsListingInfiniteScrollOptions) {
	const isSentinelIntersecting = ref(false);
	const isTriggeringLoadMore = ref(false);
	const pauseUntilNextIntersection = ref(false);
	const loadCycle = ref(0);

	function resetLoadMoreCycle(): void {
		loadCycle.value += 1;
		pauseUntilNextIntersection.value = false;
	}

	const shouldLoadMore = computed(
		() =>
			isSentinelIntersecting.value &&
			!pauseUntilNextIntersection.value &&
			hasMore.value &&
			!isLoading.value &&
			!isInfiniteLoading.value &&
			!blockListingFetches.value &&
			!isTriggeringLoadMore.value
	);

	useIntersectionObserver(
		loadMoreRef,
		([entry]) => {
			if (!entry?.isIntersecting) {
				isSentinelIntersecting.value = false;
				pauseUntilNextIntersection.value = false;
				return;
			}

			isSentinelIntersecting.value = true;
		},
		{
			rootMargin,
			threshold
		}
	);

	async function triggerLoadMore(): Promise<void> {
		if (!shouldLoadMore.value) return;

		const capturedLoadCycle = loadCycle.value;
		isTriggeringLoadMore.value = true;

		try {
			const didAppendItems = await onLoadMore();
			await nextTick();

			if (capturedLoadCycle === loadCycle.value && !didAppendItems) {
				pauseUntilNextIntersection.value = true;
			}
		} finally {
			isTriggeringLoadMore.value = false;
		}
	}

	watch(
		shouldLoadMore,
		shouldLoad => {
			if (!shouldLoad) return;
			void triggerLoadMore();
		},
		{ flush: 'post' }
	);

	const isLoadMoreArmed = computed(() => !pauseUntilNextIntersection.value);

	return {
		isSentinelIntersecting,
		isTriggeringLoadMore,
		isLoadMoreArmed,
		resetLoadMoreCycle
	};
}
